/**
 * Article Analytics Service
 * Handles tracking of article preview views, clicks, and page views
 */

import { getSession } from 'next-auth/react';

import { buildDirectBackendUrl } from './direct-backend-api';

type EventType = 'preview_view' | 'article_click' | 'page_view';

interface TrackEventParams {
  eventType: EventType;
  articleId: string;
  sessionId?: string;
}

interface TrackEventResponse {
  success: boolean;
}

/**
 * Generate or retrieve session ID for anonymous users
 */
const getSessionId = (): string => {
  if (typeof window === 'undefined') return '';

  const STORAGE_KEY = 'molwe_analytics_session';
  let sessionId = localStorage.getItem(STORAGE_KEY);

  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(STORAGE_KEY, sessionId);
  }

  return sessionId;
};

/**
 * Track article analytics event
 * @param params - Event parameters
 * @returns Promise with tracking result
 */
export const trackArticleEvent = async (
  params: Omit<TrackEventParams, 'sessionId'>,
): Promise<TrackEventResponse> => {
  try {
    // Get auth session for authorized users
    const session = await getSession();
    const sessionId = getSessionId();

    // Build headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if user is authenticated
    if (session?.accessToken) {
      headers['Authorization'] = `Bearer ${session.accessToken}`;
    }

    const response = await fetch(buildDirectBackendUrl('/api/articles-analytics-custom/track'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...params,
        sessionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Analytics tracking failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    // Silent fail - don't break user experience if analytics fails
    console.error('Failed to track article event:', error);
    return { success: false };
  }
};

/**
 * Track article preview view
 */
export const trackPreviewView = (articleId: string) => {
  return trackArticleEvent({
    eventType: 'preview_view',
    articleId,
  });
};

/**
 * Track article click
 */
export const trackArticleClick = (articleId: string) => {
  return trackArticleEvent({
    eventType: 'article_click',
    articleId,
  });
};

/**
 * Track article page view
 */
export const trackPageView = (articleId: string) => {
  return trackArticleEvent({
    eventType: 'page_view',
    articleId,
  });
};
